import {AssignmentId, AssignmentUnitTestType} from 'assignments/types';
import {
  EMPTY_TEST_RESULT,
  UnitTestResults,
} from 'assignments/unitTest/unitTestResults';
import {UNIT_TEST_TYPE_TO_SCHEMA} from 'assignments/unitTest/UnitTestSchemaRegistry';
import {UnitTestLoaderParameters} from 'assignments/unitTest/UnitTestSchema';
import {
  checkIsProjectConsole,
  checkIsProjectGraphics,
  checkIsProjectKarel,
} from 'ide/utils/general';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
import {IDEContextData} from 'ide/contexts/IDEContext';
import {
  isProjectFileStructure,
  type ProjectFilesCode,
  type ProjectFileStructure,
} from 'projects/types';
import type {LoadedAssignmentData} from 'assignments/models/loadedAssignmentData';
import type {CourseId} from 'types/common';
import type {RefObject} from 'react';
import type {ProjectDocument} from 'projects/projectDocument';

/**
 * Runs unit tests for an assignment.
 *
 * This is the core function for running unit tests. It is used by the
 * `RunUnitTestsAndReportResults` function for user facing unit test runs.
 * See `UnitTestSchema` for more information on how unit tests are run.
 *
 * @param ideContext - The IDE context.
 * @param silent - Whether to run the unit tests silently.
 * @returns The results of the unit tests.
 */
export async function runUnitTests(params: {
  assnData: LoadedAssignmentData;
  courseId: CourseId;
  isDiagnostic: boolean;
  projectData: ProjectDocument | null;
  filesCode: ProjectFilesCode;
  fileStructure: ProjectFileStructure;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  loadedUnitTestData: any;
  // TODO: TJ Implement PyodideClient type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  pyodideClientRef: RefObject<any>;
  silent: boolean;
  /**
   * The assignment milestone to check. Omitted, every unit test on the
   * assignment runs.
   */
  selectedMilestoneId?: string;
}): Promise<UnitTestResults> {
  const {
    assnData,
    courseId,
    isDiagnostic,
    projectData,
    filesCode,
    fileStructure,
    loadedUnitTestData,
    pyodideClientRef,
    silent,
    selectedMilestoneId,
  } = params;
  const assignmentId: AssignmentId = assnData?.metaData?.uid;

  if (!isProjectFileStructure(projectData?.files)) {
    console.error(
      "runUnitTests: fileStructure is not a ProjectFileStructure. We'll try to ",
      'run the unit tests anyway, but this is likely to fail.',
      {
        fileStructure: projectData?.files,
      },
    );
  }

  if (!assnData || isDiagnostic) {
    return EMPTY_TEST_RESULT;
  }
  const unitTestType: AssignmentUnitTestType =
    assnData.metaData.unitTestType ?? inferUnitTestType(assnData, projectData);
  if (!unitTestType) {
    return EMPTY_TEST_RESULT;
  }
  const loaderParameters: UnitTestLoaderParameters = {
    serverAssignmentData: assnData,
    courseId,
    assignmentId,
    projectId: projectData?.uid,
  };
  const schema = UNIT_TEST_TYPE_TO_SCHEMA[unitTestType];
  // `any` is necessary here because the loaded data is schema-specific.
  const loadedData = loadedUnitTestData;
  if (!loadedData) {
    console.warn(
      `No unit test data loaded for ${assignmentId} of type ${unitTestType}`,
    );
  }
  const pyodideClient = pyodideClientRef?.current;
  return await schema.runner({
    loadedData: loadedData ?? {},
    unitTestLoaderParameters: loaderParameters,
    filesCode,
    fileStructure,
    pyodideClient,
    silent,
    selectedMilestoneId,
  });
}

/**
 * Gets the unit test type for an assignment. For older assignments that do not
 * have a unit test type, it will attempt infer the unit test type based on the
 * provided data.
 */
export function inferUnitTestType(
  assignmentData: LoadedAssignmentData,
  projectData = null,
): AssignmentUnitTestType {
  if (!assignmentData) {
    return 'none';
  }
  if (assignmentData?.metaData?.unitTestType) {
    return assignmentData.metaData.unitTestType;
  }
  const isRandom = assignmentData?.metaData?.isRandom ?? false;
  if (isRandom) {
    return 'ai';
  }
  if (checkIsProjectKarel(projectData, assignmentData)) {
    return 'karel';
  } else if (checkIsProjectConsole(projectData, assignmentData)) {
    return 'console';
  } else if (checkIsProjectGraphics(projectData, assignmentData)) {
    return 'graphics';
  }
  console.error('No unit test type found for assignment. Defaulting to none.', {
    assignmentData,
    projectData,
  });
  return 'none';
}

export function inferUnitTestTypeFromMetadata(metadata) {
  return inferUnitTestType({metaData: metadata});
}
